import { useEffect, useMemo, useState } from 'react';
import { fetchBacklog } from '../api';
import { useAuth } from '../auth/AuthProvider';
import { useAppData } from '../AppData';
import { BacklogTask, STATUS_LABELS, VISIBLE_STATUSES } from '../domain/types';
import { nameByEmail, errMsg, taskHeading, taskSummary, diaFecha, diaLocal, diasEntre, fmtDia } from '../domain/util';
import TaskModal from '../components/TaskModal';

const PROY_STATUS_LABELS: Record<string, string> = {
  planning: 'Planificación',
  in_progress: 'En progreso',
  paused: 'Pausado',
  done: 'Finalizado',
  cancelled: 'Cancelado',
};

// El deadline vale hasta el final de su día: una tarea vence recién al día
// siguiente. Ver estadoEntrega() en domain/normalize.ts.
function isOverdue(t: BacklogTask): boolean {
  const deadline = diaFecha(t.deadline);
  return !!deadline && diaLocal(new Date()) > deadline && !['done', 'cancelled'].includes(t.status);
}

function isDueSoon(t: BacklogTask): boolean {
  if (!t.deadline || isOverdue(t) || ['done', 'cancelled'].includes(t.status)) return false;
  const faltan = diasEntre(diaLocal(new Date()), diaFecha(t.deadline));
  return faltan != null && faltan >= 0 && faltan <= 7;
}

export default function Backlog() {
  const { role, email } = useAuth();
  const { users, dimensions } = useAppData();
  const [all, setAll] = useState<BacklogTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState<BacklogTask | null>(null);

  // filtros
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [urg, setUrg] = useState('');
  const [projStatusFilter, setProjStatusFilter] = useState<Set<string>>(new Set());

  // mapa proyecto -> estado
  const projStatusMap = useMemo(() => {
    const m = new Map<string, string>();
    (dimensions.proyecto || []).forEach((p) => m.set(p.name, p.status || ''));
    return m;
  }, [dimensions.proyecto]);
  const toggleProjStatus = (s: string) =>
    setProjStatusFilter((prev) => {
      const n = new Set(prev);
      n.has(s) ? n.delete(s) : n.add(s);
      return n;
    });

  // proyectos colapsados (persistido)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('bl.collapsed') || '[]')); } catch { return new Set(); }
  });
  const toggleGroup = (key: string) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      localStorage.setItem('bl.collapsed', JSON.stringify([...n]));
      return n;
    });

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      setAll(await fetchBacklog(role!, email));
    } catch (e) {
      setErr(errMsg(e));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [role, email]);

  const filtered = useMemo(() => {
    let arr = all;
    if (statusFilter.size) arr = arr.filter((t) => statusFilter.has(t.status));
    if (urg) arr = arr.filter((t) => t.urgencia === urg);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (t) =>
          t.description.toLowerCase().includes(q) ||
          t.titulo.toLowerCase().includes(q) ||
          t.assigned_to.toLowerCase().includes(q) ||
          t.proyecto.toLowerCase().includes(q) ||
          t.espacio.toLowerCase().includes(q) ||
          t.cliente_interno.toLowerCase().includes(q)
      );
    }
    if (projStatusFilter.size) {
      arr = arr.filter((t) => t.proyecto && projStatusFilter.has(projStatusMap.get(t.proyecto) || ''));
    }
    return arr;
  }, [all, statusFilter, urg, search, projStatusFilter, projStatusMap]);

  // Agrupar por proyecto (con su estado). "Sin proyecto" va al final.
  const groups = useMemo(() => {
    const m = new Map<string, BacklogTask[]>();
    for (const t of filtered) {
      const key = t.proyecto || '';
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(t);
    }
    return [...m.entries()]
      .sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : a[0].localeCompare(b[0])))
      .map(([name, tasks]) => ({ name, status: projStatusMap.get(name) || '', tasks }));
  }, [filtered, projStatusMap]);

  const toggleStatus = (s: string) =>
    setStatusFilter((prev) => {
      const n = new Set(prev);
      n.has(s) ? n.delete(s) : n.add(s);
      return n;
    });

  const overdueCount = useMemo(() => filtered.filter(isOverdue).length, [filtered]);
  const dueSoonCount = useMemo(() => filtered.filter(isDueSoon).length, [filtered]);
  const activeCount = useMemo(() => filtered.filter((t) => !['done', 'cancelled'].includes(t.status)).length, [filtered]);
  const activeFilterCount = statusFilter.size + projStatusFilter.size + (urg ? 1 : 0) + (search.trim() ? 1 : 0);
  const clearFilters = () => {
    setSearch('');
    setStatusFilter(new Set());
    setUrg('');
    setProjStatusFilter(new Set());
  };

  return (
    <div className="grid">
      <div className="panel page-hero backlog-hero">
        <div className="backlog-hero-copy">
          <div className="eyebrow">Gestión de tareas</div>
          <h1>Backlog</h1>
          <p className="muted">
            Vista agrupada por proyecto para priorizar pendientes, detectar vencimientos y entrar rápido al detalle.
          </p>
        </div>
        <div className="stat-strip" aria-label="Resumen de tareas filtradas">
          <div className="stat-card">
            <span className="stat-value">{filtered.length}</span>
            <span className="stat-label">{filtered.length === 1 ? 'tarea visible' : 'tareas visibles'}</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{activeCount}</span>
            <span className="stat-label">activas</span>
          </div>
          <div className={`stat-card ${dueSoonCount > 0 ? 'warning' : ''}`}>
            <span className="stat-value">{dueSoonCount}</span>
            <span className="stat-label">próx. 7 días</span>
          </div>
          <div className={`stat-card ${overdueCount > 0 ? 'danger' : ''}`}>
            <span className="stat-value">{overdueCount}</span>
            <span className="stat-label">vencidas</span>
          </div>
        </div>
      </div>

      <details className="panel filters-panel" open>
        <summary>
          <span className="filters-title">Filtros</span>
          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount} activo{activeFilterCount === 1 ? '' : 's'}</span>}
        </summary>
        <div className="row filters-grid">
          <div style={{ flex: 2, minWidth: 220 }}>
            <label>Buscar</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="descripción, título, proyecto, espacio, asignado…" />
          </div>
          <div style={{ minWidth: 140 }}>
            <label>Urgencia</label>
            <select value={urg} onChange={(e) => setUrg(e.target.value)}>
              <option value="">Todas</option>
              <option value="baja">baja</option>
              <option value="media">media</option>
              <option value="alta">alta</option>
            </select>
          </div>
          {activeFilterCount > 0 && (
            <div className="filter-actions">
              <button className="ghost sm" type="button" onClick={clearFilters}>Limpiar filtros</button>
            </div>
          )}
        </div>
        <label style={{ marginTop: 12 }}>Estado de tarea</label>
        <div className="row filter-chip-row">
          {VISIBLE_STATUSES.map((s) => (
            <span key={s} className={`chip ${statusFilter.has(s) ? 'active' : ''}`} style={{ cursor: 'pointer' }} onClick={() => toggleStatus(s)}>
              {STATUS_LABELS[s]}
            </span>
          ))}
        </div>

        <label style={{ marginTop: 14 }}>Estado de proyecto</label>
        <div className="row inline-checks">
          {Object.entries(PROY_STATUS_LABELS).map(([val, label]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0, fontWeight: 500, color: 'var(--text)', cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={projStatusFilter.has(val)} onChange={() => toggleProjStatus(val)} />
              {label}
            </label>
          ))}
        </div>
      </details>

      {err && <div className="error">{err}</div>}

      {loading ? (
        <div className="panel"><p className="muted">Cargando…</p></div>
      ) : groups.length === 0 ? (
        <div className="panel"><p className="muted">No hay tareas que coincidan.</p></div>
      ) : (
        groups.map((g) => {
          const key = g.name || '__none';
          const isCollapsed = collapsed.has(key);
          const overdueInGroup = g.tasks.filter(isOverdue).length;
          return (
          <div className="panel-flush proj-group" key={key}>
            <div className="proj-head" role="button" aria-expanded={!isCollapsed} onClick={() => toggleGroup(key)}>
              <span className={`proj-chevron ${isCollapsed ? '' : 'open'}`}>▸</span>
              <span className="proj-name">{g.name || 'Sin proyecto'}</span>
              {g.name && <span className={`badge pst-${g.status || 'none'}`}>{PROY_STATUS_LABELS[g.status] || g.status || 'sin estado'}</span>}
              {overdueInGroup > 0 && <span className="badge urg-alta">{overdueInGroup} vencida{overdueInGroup === 1 ? '' : 's'}</span>}
              <span className="spacer" />
              <span className="proj-count">{g.tasks.length} {g.tasks.length === 1 ? 'tarea' : 'tareas'}</span>
            </div>
            {!isCollapsed && (
            <table>
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Título</th>
                  <th>Espacio / Cliente</th>
                  <th>Asignado</th>
                  <th>Urg.</th>
                  <th>Deadline</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {g.tasks.map((t) => {
                  const canEdit = role === 'admin' || (t.assigned_to.toLowerCase() === email && t.status !== 'done');
                  const overdue = isOverdue(t);
                  const dueSoon = isDueSoon(t);
                  return (
                    <tr key={t.id} className={`clickable ${overdue ? 'row-overdue' : ''}`} onClick={() => setOpen(t)}>
                      <td><span className={`badge st-${t.status}`}>{STATUS_LABELS[t.status]}</span></td>
                      <td className="task-desc">
                        {/* El título identifica la tarea; sin título (tareas previas al campo) va la descripción. */}
                        <div className="task-title">{taskHeading(t.titulo, t.description, 96)}</div>
                        <div className="task-meta">{taskSummary(t) || 'Sin categoría asignada'}</div>
                      </td>
                      <td>
                        <div className="stacked-cell">
                          <span>{t.espacio || '—'}</span>
                          <span className="cell-muted">{t.cliente_interno || 'Sin cliente'}</span>
                        </div>
                      </td>
                      <td>
                        <div className="stacked-cell">
                          <span>{nameByEmail(t.assigned_to, users)}</span>
                          <span className="cell-muted">{t.assigned_to}</span>
                        </div>
                      </td>
                      <td>{t.urgencia ? <span className={`badge urg-${t.urgencia}`}>{t.urgencia}</span> : '—'}</td>
                      <td className="num">
                        <span className={`deadline-pill ${overdue ? 'overdue' : dueSoon ? 'due-soon' : ''}`}>
                          {fmtDia(t.deadline)}
                          {overdue && ' ⚠'}
                        </span>
                      </td>
                      <td><button className="ghost sm" onClick={(e) => { e.stopPropagation(); setOpen(t); }}>{canEdit ? 'Editar' : 'Ver'}</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            )}
          </div>
          );
        })
      )}

      {open && <TaskModal task={open} allTasks={all} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}
